import { useCallback, useMemo } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  HiOutlineArrowLongRight,
  HiOutlineBuildingOffice2,
  HiOutlineEnvelope,
  HiOutlineServer,
  HiOutlineSquaresPlus,
} from "react-icons/hi2";

import { TargetColumn } from "@/hooks/useCsvUploader";
import FormDropdown from "@/components/molecules/form/FormDropdown";
import { searchColumns } from "@/components/organisms/search/searchColumns";

export const BASE_OPTIONS = (t: TFunction): Option[] => [
  {
    icon: HiOutlineBuildingOffice2,
    name: searchColumns(t).company_name.name,
    id: TargetColumn.COMPANY_NAME,
    disabled: false,
  },
  {
    icon: HiOutlineServer,
    name: searchColumns(t).domains.name,
    id: TargetColumn.DOMAIN,
    disabled: false,
  },
  {
    icon: HiOutlineEnvelope,
    name: searchColumns(t).contact_info_email.name,
    id: TargetColumn.EMAIL,
    disabled: false,
  },
];

type SelectProps = {
  selectedValue: string;
  onChange: (value: string | string[]) => void;
  column: string;
  options: Option[];
  disabledOptions: string[];
  hasError?: boolean;
  helperText?: string;
};

type Option = {
  name: string;
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled: boolean;
};

const ColumnMapSelectItem = ({
  selectedValue,
  onChange,
  column,
  options,
  disabledOptions,
  hasError,
  helperText,
}: SelectProps) => {
  const { t } = useTranslation();

  // Determine which options should be disabled
  const enhancedOptions = useMemo(() => {
    return options.map((option) => ({
      ...option,
      disabled: option.disabled || disabledOptions.includes(option.id),
    }));
  }, [options, disabledOptions]);

  const renderIconAndName = useCallback(
    (option: Option) => (
      <div className="flex items-center gap-2">
        <option.icon className="size-5" />
        <span>{option.name}</span>
      </div>
    ),
    [],
  );

  const placeholderContent = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <HiOutlineSquaresPlus className="size-5" />
        <span>{t("enrich.map.select")}</span>
      </div>
    ),
    [t],
  );

  return (
    <div className="grid grid-cols-2 items-center px-4 py-3 text-sm font-medium">
      <div className="flex justify-between pr-10">
        <p className="truncate">{column}</p>
        <HiOutlineArrowLongRight className="size-5" />
      </div>

      <FormDropdown
        name={`column.${column}`}
        options={enhancedOptions}
        value={selectedValue}
        onChange={onChange}
        placeholder={placeholderContent}
        renderPlaceholder={placeholderContent}
        displayValue={renderIconAndName}
        renderOption={renderIconAndName}
        hasError={hasError}
        helperText={helperText}
      />
    </div>
  );
};

export default ColumnMapSelectItem;
